import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { passwordSchema } from '@mefosa/shared'
import { api, ApiRequestError } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { BRAND } from '../theme'

export default function ChangePasswordPage() {
  const { t } = useTranslation()
  const { refresh, signOut } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const policy = passwordSchema.safeParse(newPassword)
  const policyError = newPassword && !policy.success ? policy.error.issues[0]?.message : undefined

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (newPassword !== confirm) {
      setError(t('auth.passwordsDoNotMatch'))
      return
    }

    setBusy(true)
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword })
      await refresh()
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.code === 'NETWORK' ? t('errors.network') : err.message
          : t('errors.generic'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        background: `linear-gradient(160deg, ${BRAND.navy} 0%, #0A2545 55%, #071B33 100%)`,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h6" component="h1">{t('auth.mustChangeTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {t('auth.mustChangeBody')}
          </Typography>

          <form onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label={t('auth.currentPassword')}
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
                fullWidth
              />
              <TextField
                label={t('auth.newPassword')}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                error={Boolean(policyError)}
                helperText={policyError}
                required
                fullWidth
              />
              <TextField
                label={t('auth.confirmPassword')}
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                error={Boolean(confirm) && confirm !== newPassword}
                helperText={confirm && confirm !== newPassword ? t('auth.passwordsDoNotMatch') : undefined}
                required
                fullWidth
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy || !policy.success || newPassword !== confirm}
                fullWidth
              >
                {busy ? t('common.saving') : t('auth.changePassword')}
              </Button>
              <Button onClick={() => void signOut()} size="small">{t('auth.signOut')}</Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}
